import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { BLOG_CATEGORY_IDS } from "./blog-categories";
import { assertOwnershipForPublish } from "./blog-ownership-validation";

/**
 * Blog / Insights data access.
 *
 * Mirrors the conventions in resources.functions.ts: a keyless public client
 * for anon reads (RLS restricts rows to status = 'published'), the shared
 * service-role client for admin writes, and TanStack Start server functions
 * wired through the query registry.
 */

function publicSupabase() {
  const url = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY || process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) {
    throw new Error("Missing SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY");
  }
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

function blogTable(client: unknown): any {
  return (client as any).from("blog_posts");
}

/**
 * Scheduled publishing.
 *
 * A post is LIVE only when status = 'published' AND published_at IS NOT NULL
 * AND published_at <= now(). A published post with a future published_at is
 * "scheduled": invisible on every public surface until the timestamp passes.
 *
 * RLS enforces exactly this window for anon (see
 * supabase/migrations/*_blog_scheduled_publishing.sql); the query-level filter
 * below restates it so the intent is explicit in application code too.
 */
function liveBlogQuery(client: unknown, fields: string): any {
  return blogTable(client)
    .select(fields)
    .eq("status", "published")
    .not("published_at", "is", null)
    .lte("published_at", new Date().toISOString());
}

function resourceTable(client: unknown): any {
  return (client as any).from("resources");
}

async function requireAdmin(context: any) {
  const { supabase, userId } = context;
  const { data: roleRow } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!roleRow) throw new Error("Forbidden");
}

// ---------------------------------------------------------------------------
// Field sets
// ---------------------------------------------------------------------------

const CARD_FIELDS =
  "id, slug, title, excerpt, featured_image, featured_image_alt, author, published_at, updated_at, created_at, category, tags, reading_minutes, featured, noindex, status";

const RELATED_RESOURCE_FIELDS =
  "slug, slug_es, title_en, excerpt_en, cover_image_url, tags_en, reading_minutes";

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const NullableText = (max: number) => z.string().trim().max(max).nullable().optional();
const Slug = z
  .string()
  .trim()
  .max(120)
  .regex(/^[a-z0-9-]*$/, "lowercase letters, numbers, hyphens only");

export const BlogPostInputSchema = z.object({
  id: z.string().uuid().optional(),
  slug: Slug.min(1),
  slug_es: Slug.nullable().optional(),
  title_es: NullableText(200),
  excerpt_es: NullableText(400),
  content_es: z.string().max(200000).nullable().optional(),
  meta_title_es: NullableText(200),
  meta_description_es: NullableText(320),
  title: z.string().trim().min(1).max(200),
  excerpt: z.string().trim().max(400).default(""),
  content: z.string().max(200000).default(""),
  featured_image: NullableText(500),
  featured_image_alt: NullableText(300),
  author: z.string().trim().min(1).max(120).default("Samuel Noriega"),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  published_at: NullableText(64),
  category: z.enum(BLOG_CATEGORY_IDS as [string, ...string[]]),
  tags: z.array(z.string().trim().min(1).max(60)).max(12).default([]),
  reading_minutes: z.number().int().min(1).max(120).default(6),
  featured: z.boolean().default(false),
  sort_order: z.number().int().default(0),
  meta_title: NullableText(200),
  meta_description: NullableText(320),
  canonical_url: NullableText(500),
  focus_keyword: NullableText(120),
  secondary_keywords: z.array(z.string().trim().min(1).max(120)).max(15).default([]),
  og_title: NullableText(200),
  og_description: NullableText(320),
  og_image: NullableText(500),
  twitter_title: NullableText(200),
  twitter_description: NullableText(320),
  schema_type: z.enum(["BlogPosting", "Article"]).default("BlogPosting"),
  noindex: z.boolean().default(false),
  related_resources: z.array(z.string().trim().min(1).max(160)).max(12).default([]),
  related_services: z.array(z.string().trim().min(1).max(200)).max(12).default([]),
  related_case_studies: z.array(z.string().trim().min(1).max(160)).max(12).default([]),
  related_blog_posts: z.array(z.string().trim().min(1).max(160)).max(12).default([]),
  primary_topic: NullableText(160),
  search_intent: z
    .enum(["informational", "commercial-investigation", "transactional", "navigational"])
    .nullable()
    .optional(),
  cannibalization_notes: NullableText(2000),
});

export type BlogPostInput = z.infer<typeof BlogPostInputSchema>;

const SlugSchema = z.object({ slug: Slug.min(1) });
const IdSchema = z.object({ id: z.string().uuid() });
const CategorySchema = z.object({
  category: z.enum(BLOG_CATEGORY_IDS as [string, ...string[]]),
});
const PageSchema = z.object({
  slug: Slug.min(1),
  relatedLimit: z.number().int().min(1).max(6).default(3),
});

// ---------------------------------------------------------------------------
// Public reads
// ---------------------------------------------------------------------------

export const listPublicBlogPosts = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const { data, error } = await liveBlogQuery(publicSupabase(), CARD_FIELDS)
      .order("published_at", { ascending: false })
      .order("sort_order", { ascending: true });

    if (error) {
      console.error("listPublicBlogPosts error", error);
      return { posts: [] as any[] };
    }
    return { posts: data ?? [] };
  } catch (error) {
    console.error("listPublicBlogPosts exception", error);
    return { posts: [] as any[] };
  }
});

export const listPublicBlogPostsByCategory = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => CategorySchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const { data: rows, error } = await liveBlogQuery(publicSupabase(), CARD_FIELDS)
        .eq("category", data.category)
        .order("published_at", { ascending: false })
        .order("sort_order", { ascending: true });

      if (error) {
        console.error("listPublicBlogPostsByCategory error", error);
        return { posts: [] as any[] };
      }
      return { posts: rows ?? [] };
    } catch (error) {
      console.error("listPublicBlogPostsByCategory exception", error);
      return { posts: [] as any[] };
    }
  });

/**
 * Category -> live-post count, for the index filter and sitemap gate.
 * Scheduled (future-dated) posts are not counted until they go live.
 */
export const listPublicBlogCategoryCounts = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const { data, error } = await liveBlogQuery(publicSupabase(), "category");

    if (error) {
      console.error("listPublicBlogCategoryCounts error", error);
      return { counts: {} as Record<string, number> };
    }
    const counts: Record<string, number> = {};
    for (const row of (data ?? []) as { category: string }[]) {
      counts[row.category] = (counts[row.category] ?? 0) + 1;
    }
    return { counts };
  } catch (error) {
    console.error("listPublicBlogCategoryCounts exception", error);
    return { counts: {} as Record<string, number> };
  }
});

function pickRelated(
  rows: any[],
  currentSlug: string,
  category: string,
  limit: number,
  preferSlugs: string[],
) {
  const bySlug = new Map<string, any>();
  for (const row of rows) bySlug.set(row.slug, row);
  const out: any[] = [];
  const seen = new Set<string>([currentSlug]);
  for (const slug of preferSlugs) {
    const row = bySlug.get(slug);
    if (row && !seen.has(slug)) {
      out.push(row);
      seen.add(slug);
    }
    if (out.length >= limit) return out;
  }
  for (const row of rows) {
    if (out.length >= limit) break;
    if (seen.has(row.slug)) continue;
    if (row.category === category) {
      out.push(row);
      seen.add(row.slug);
    }
  }
  for (const row of rows) {
    if (out.length >= limit) break;
    if (seen.has(row.slug)) continue;
    out.push(row);
    seen.add(row.slug);
  }
  return out;
}

/**
 * Page-level read for /blog/:slug. Fetches the post, a ranked set of related
 * posts and the metadata for any linked resources / case studies in one round
 * of concurrent queries so the article renders without a request waterfall.
 */
export const getPublicBlogPostPage = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => PageSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const client = publicSupabase();
      const [postResult, candidatesResult] = await Promise.all([
        liveBlogQuery(client, "*").eq("slug", data.slug).maybeSingle(),
        liveBlogQuery(client, CARD_FIELDS).order("published_at", { ascending: false }).limit(60),
      ]);

      if (postResult.error) {
        console.error("getPublicBlogPostPage post error", postResult.error);
        return {
          post: null,
          relatedPosts: [] as any[],
          relatedResources: [] as any[],
          relatedCaseStudies: [] as any[],
        };
      }
      const post = postResult.data;
      if (!post) {
        return {
          post: null,
          relatedPosts: [] as any[],
          relatedResources: [] as any[],
          relatedCaseStudies: [] as any[],
        };
      }

      const candidates = (candidatesResult.data ?? []) as any[];
      const relatedPosts = pickRelated(
        candidates,
        post.slug,
        post.category,
        data.relatedLimit,
        (post.related_blog_posts ?? []) as string[],
      );

      const resourceSlugs = Array.from(
        new Set([
          ...((post.related_resources ?? []) as string[]),
          ...((post.related_case_studies ?? []) as string[]),
        ]),
      ).filter(Boolean);

      let resourceRows: any[] = [];
      if (resourceSlugs.length > 0) {
        const { data: rows, error } = await resourceTable(client)
          .select(RELATED_RESOURCE_FIELDS)
          .eq("published", true)
          .in("slug", resourceSlugs);
        if (error) console.error("getPublicBlogPostPage related resources error", error);
        else resourceRows = rows ?? [];
      }
      const rowBySlug = new Map(resourceRows.map((r) => [r.slug, r]));
      const relatedResources = ((post.related_resources ?? []) as string[])
        .map((slug) => rowBySlug.get(slug))
        .filter(Boolean);
      const relatedCaseStudies = ((post.related_case_studies ?? []) as string[])
        .map((slug) => rowBySlug.get(slug))
        .filter(Boolean);

      return { post, relatedPosts, relatedResources, relatedCaseStudies };
    } catch (error) {
      console.error("getPublicBlogPostPage exception", error);
      return {
        post: null,
        relatedPosts: [] as any[],
        relatedResources: [] as any[],
        relatedCaseStudies: [] as any[],
      };
    }
  });

// ---------------------------------------------------------------------------
// Admin reads + writes
// ---------------------------------------------------------------------------

export const listAdminBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await blogTable(supabaseAdmin)
      .select(
        "id, slug, slug_es, title, title_es, content_es, category, status, featured, published_at, updated_at, reading_minutes, noindex, tags, related_resources, related_services",
      )
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { posts: data ?? [] };
  });

export const getBlogPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await blogTable(supabaseAdmin)
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { post: row };
  });

export const upsertBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => BlogPostInputSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Validate before any write, including the featured-post side effect.
    let previousStatus: string | undefined;
    if (data.id) {
      const { data: existing, error } = await blogTable(supabaseAdmin)
        .select("status")
        .eq("id", data.id)
        .single();
      if (error) throw new Error(error.message);
      previousStatus = existing.status;
    }
    assertOwnershipForPublish(data, previousStatus);

    const payload = {
      slug: data.slug,
      slug_es: data.slug_es || null,
      ...(data.title_es !== undefined ? { title_es: data.title_es || null } : {}),
      ...(data.excerpt_es !== undefined ? { excerpt_es: data.excerpt_es || null } : {}),
      ...(data.content_es !== undefined ? { content_es: data.content_es || null } : {}),
      ...(data.meta_title_es !== undefined ? { meta_title_es: data.meta_title_es || null } : {}),
      ...(data.meta_description_es !== undefined
        ? { meta_description_es: data.meta_description_es || null }
        : {}),
      title: data.title,
      excerpt: data.excerpt,
      content: data.content,
      featured_image: data.featured_image || null,
      featured_image_alt: data.featured_image_alt || null,
      author: data.author,
      status: data.status,
      published_at: data.published_at || null,
      category: data.category,
      tags: data.tags,
      reading_minutes: data.reading_minutes,
      featured: data.featured,
      sort_order: data.sort_order,
      meta_title: data.meta_title || null,
      meta_description: data.meta_description || null,
      canonical_url: data.canonical_url || null,
      focus_keyword: data.focus_keyword || null,
      secondary_keywords: data.secondary_keywords,
      og_title: data.og_title || null,
      og_description: data.og_description || null,
      og_image: data.og_image || null,
      twitter_title: data.twitter_title || null,
      twitter_description: data.twitter_description || null,
      schema_type: data.schema_type,
      noindex: data.noindex,
      related_resources: data.related_resources,
      related_services: data.related_services,
      related_case_studies: data.related_case_studies,
      related_blog_posts: data.related_blog_posts,
      primary_topic: data.primary_topic || null,
      search_intent: data.search_intent ?? null,
      cannibalization_notes: data.cannibalization_notes || null,
    };

    // Single featured post at a time, mirroring the resources admin behaviour.
    if (data.featured) {
      const query = blogTable(supabaseAdmin).update({ featured: false }).eq("featured", true);
      const { error } = data.id ? await query.neq("id", data.id) : await query;
      if (error) throw new Error(error.message);
    }

    if (data.id) {
      const { data: row, error } = await blogTable(supabaseAdmin)
        .update(payload)
        .eq("id", data.id)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      return { id: row.id as string };
    }

    const { data: row, error } = await blogTable(supabaseAdmin)
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await blogTable(supabaseAdmin).delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export { SlugSchema as BlogSlugSchema };

/** Spanish articles are available only when their full localized body is present. */
export const listPublicSpanishBlogPosts = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await liveBlogQuery(
    publicSupabase(),
    "slug,slug_es,title_es,excerpt_es,published_at,updated_at,category,noindex,reading_minutes,author,featured_image_es,featured_image_alt_es",
  )
    .not("slug_es", "is", null)
    .not("title_es", "is", null)
    .not("content_es", "is", null)
    .neq("content_es", "")
    .neq("title_es", "")
    .order("published_at", { ascending: false });
  if (error) throw new Error(error.message);
  return { posts: (data ?? []).filter((p: any) => p.slug_es?.trim() && p.title_es?.trim()) };
});

type SpanishSibling = {
  slug_es: string | null;
  title_es: string | null;
  excerpt_es: string | null;
  category: string | null;
  published_at: string;
};

export const getPublicSpanishBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => SlugSchema.parse(input))
  .handler(async ({ data }) => {
    const { data: post, error } = await liveBlogQuery(
      publicSupabase(),
      "slug,slug_es,title,title_es,excerpt_es,content_es,meta_title_es,meta_description_es,og_image_es,featured_image_es,featured_image_alt_es,og_image,featured_image,published_at,updated_at,category,noindex,reading_minutes,author",
    )
      .eq("slug_es", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const live =
      post?.slug_es?.trim() && post?.title_es?.trim() && post?.content_es?.trim() ? post : null;
    if (!live) return { post: null, related: [] };
    // Same-category Spanish siblings first, then the newest others, so every
    // article links to related reading inside its own topic cluster.
    const { data: siblings } = await liveBlogQuery(
      publicSupabase(),
      "slug_es,title_es,excerpt_es,category,published_at",
    )
      .not("slug_es", "is", null)
      .not("title_es", "is", null)
      .not("content_es", "is", null)
      .neq("slug_es", live.slug_es)
      .order("published_at", { ascending: false })
      .limit(40);
    const pool = (siblings ?? []).filter(
      (p: SpanishSibling) => p.slug_es?.trim() && p.title_es?.trim(),
    );
    const related = [
      ...pool.filter((p: SpanishSibling) => p.category === live.category),
      ...pool.filter((p: SpanishSibling) => p.category !== live.category),
    ].slice(0, 3);
    return { post: live, related };
  });
